import { createSlice, nanoid } from '@reduxjs/toolkit';

// Report types
export const REPORT_TYPE = {
  REVENUE: 'revenue',
  EXPENSES: 'expenses',
  PROFIT_LOSS: 'profit_loss',
  DRIVER_EARNINGS: 'driver_earnings',
  COMMISSION: 'commission',
  TAX: 'tax',
  REFUNDS: 'refunds'
};

// Time periods
export const TIME_PERIOD = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  YEARLY: 'yearly'
};

// Generate realistic financial data
const generateFinancialData = () => {
  const currentDate = new Date();
  const data = {
    revenue: {
      daily: [],
      weekly: [],
      monthly: [],
      quarterly: [],
      yearly: []
    },
    expenses: {
      daily: [],
      weekly: [],
      monthly: [],
      quarterly: [],
      yearly: []
    },
    driverEarnings: {
      daily: [],
      weekly: [],
      monthly: [],
      quarterly: [],
      yearly: []
    }
  };

  // Generate daily data for last 30 days
  for (let i = 29; i >= 0; i--) {
    const date = new Date(currentDate);
    date.setDate(date.getDate() - i);
    
    const revenue = parseFloat((Math.random() * 5000 + 2000).toFixed(2));
    const expenses = parseFloat((revenue * (0.15 + Math.random() * 0.1)).toFixed(2));
    const driverEarnings = parseFloat((revenue * (0.75 + Math.random() * 0.1)).toFixed(2));
    
    data.revenue.daily.push({
      date: date.toISOString().split('T')[0],
      value: revenue,
      transactions: Math.floor(Math.random() * 200 + 50)
    });
    
    data.expenses.daily.push({
      date: date.toISOString().split('T')[0],
      value: expenses,
      categories: {
        marketing: parseFloat((expenses * 0.3).toFixed(2)),
        operations: parseFloat((expenses * 0.4).toFixed(2)),
        technology: parseFloat((expenses * 0.2).toFixed(2)),
        other: parseFloat((expenses * 0.1).toFixed(2))
      }
    });
    
    data.driverEarnings.daily.push({
      date: date.toISOString().split('T')[0],
      value: driverEarnings,
      activeDrivers: Math.floor(Math.random() * 50 + 20)
    });
  }

  // Generate weekly data for last 12 weeks
  for (let i = 11; i >= 0; i--) {
    const startDate = new Date(currentDate);
    startDate.setDate(startDate.getDate() - (i * 7));
    
    const revenue = parseFloat((Math.random() * 30000 + 15000).toFixed(2));
    const expenses = parseFloat((revenue * (0.15 + Math.random() * 0.1)).toFixed(2));
    const driverEarnings = parseFloat((revenue * (0.75 + Math.random() * 0.1)).toFixed(2));
    
    data.revenue.weekly.push({
      week: `Week ${12 - i}`,
      startDate: startDate.toISOString().split('T')[0],
      value: revenue,
      transactions: Math.floor(Math.random() * 1500 + 500)
    });
    
    data.expenses.weekly.push({
      week: `Week ${12 - i}`,
      startDate: startDate.toISOString().split('T')[0],
      value: expenses,
      categories: {
        marketing: parseFloat((expenses * 0.3).toFixed(2)),
        operations: parseFloat((expenses * 0.4).toFixed(2)),
        technology: parseFloat((expenses * 0.2).toFixed(2)),
        other: parseFloat((expenses * 0.1).toFixed(2))
      }
    });
    
    data.driverEarnings.weekly.push({
      week: `Week ${12 - i}`,
      startDate: startDate.toISOString().split('T')[0],
      value: driverEarnings,
      activeDrivers: Math.floor(Math.random() * 300 + 150)
    });
  }

  // Generate monthly data for last 12 months
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  for (let i = 11; i >= 0; i--) {
    const date = new Date(currentDate);
    date.setMonth(date.getMonth() - i);
    
    const revenue = parseFloat((Math.random() * 120000 + 80000).toFixed(2));
    const expenses = parseFloat((revenue * (0.15 + Math.random() * 0.1)).toFixed(2));
    const driverEarnings = parseFloat((revenue * (0.75 + Math.random() * 0.1)).toFixed(2));
    
    data.revenue.monthly.push({
      month: months[date.getMonth()],
      year: date.getFullYear(),
      value: revenue,
      transactions: Math.floor(Math.random() * 6000 + 3000)
    });
    
    data.expenses.monthly.push({
      month: months[date.getMonth()],
      year: date.getFullYear(),
      value: expenses,
      categories: {
        marketing: parseFloat((expenses * 0.3).toFixed(2)),
        operations: parseFloat((expenses * 0.4).toFixed(2)),
        technology: parseFloat((expenses * 0.2).toFixed(2)),
        other: parseFloat((expenses * 0.1).toFixed(2))
      }
    });
    
    data.driverEarnings.monthly.push({
      month: months[date.getMonth()],
      year: date.getFullYear(),
      value: driverEarnings,
      activeDrivers: Math.floor(Math.random() * 1200 + 800)
    });
  }

  return data;
};

// Generate KPI data
const generateKPIs = () => {
  return {
    totalRevenue: {
      current: 1247893.45,
      previous: 1156234.78,
      change: 7.9,
      trend: 'up'
    },
    totalExpenses: {
      current: 234567.89,
      previous: 245678.90,
      change: -4.5,
      trend: 'down'
    },
    netProfit: {
      current: 1013325.56,
      previous: 910555.88,
      change: 11.3,
      trend: 'up'
    },
    averageOrderValue: {
      current: 24.67,
      previous: 23.45,
      change: 5.2,
      trend: 'up'
    },
    totalTransactions: {
      current: 50567,
      previous: 49234,
      change: 2.7,
      trend: 'up'
    },
    driverEarnings: {
      current: 934420.09,
      previous: 867176.85,
      change: 7.8,
      trend: 'up'
    },
    commissionRevenue: {
      current: 313473.36,
      previous: 289057.93,
      change: 8.4,
      trend: 'up'
    },
    refundRate: {
      current: 2.3,
      previous: 2.8,
      change: -17.9,
      trend: 'down'
    }
  };
};

// Generate saved reports
const generateSavedReports = () => {
  return [
    {
      id: nanoid(),
      name: 'Monthly Revenue Report',
      type: REPORT_TYPE.REVENUE,
      period: TIME_PERIOD.MONTHLY,
      description: 'Comprehensive monthly revenue analysis with breakdowns',
      createdBy: 'admin',
      createdAt: '2024-12-01T10:30:00.000Z',
      lastGenerated: '2024-12-06T09:15:00.000Z',
      isScheduled: true,
      scheduleFrequency: 'monthly',
      recipients: ['finance@cabtrack.com', 'admin@cabtrack.com']
    },
    {
      id: nanoid(),
      name: 'Driver Earnings Summary',
      type: REPORT_TYPE.DRIVER_EARNINGS,
      period: TIME_PERIOD.WEEKLY,
      description: 'Weekly driver earnings and performance metrics',
      createdBy: 'hr_manager',
      createdAt: '2024-11-15T14:20:00.000Z',
      lastGenerated: '2024-12-06T08:00:00.000Z',
      isScheduled: true,
      scheduleFrequency: 'weekly',
      recipients: ['hr@cabtrack.com', 'drivers@cabtrack.com']
    },
    {
      id: nanoid(),
      name: 'Quarterly P&L Statement',
      type: REPORT_TYPE.PROFIT_LOSS,
      period: TIME_PERIOD.QUARTERLY,
      description: 'Detailed profit and loss statement for quarterly review',
      createdBy: 'cfo',
      createdAt: '2024-10-01T16:45:00.000Z',
      lastGenerated: '2024-12-01T12:00:00.000Z',
      isScheduled: false,
      scheduleFrequency: null,
      recipients: ['board@cabtrack.com', 'finance@cabtrack.com']
    }
  ];
};

const initialState = {
  financialData: generateFinancialData(),
  kpis: generateKPIs(),
  savedReports: generateSavedReports(),
  currentReport: null,
  isGenerating: false,
  lastUpdated: new Date().toISOString()
};

const financeReportSlice = createSlice({
  name: 'financeReports',
  initialState,
  reducers: {
    generateReport(state, action) {
      const { reportType, period, dateRange } = action.payload;
      state.isGenerating = true;
      state.currentReport = {
        id: nanoid(),
        type: reportType,
        period,
        dateRange,
        generatedAt: new Date().toISOString(),
        status: 'generating'
      };
    },

    completeReportGeneration(state, action) {
      state.isGenerating = false;
      if (state.currentReport) {
        state.currentReport = {
          ...state.currentReport,
          ...action.payload,
          status: 'completed',
          completedAt: new Date().toISOString()
        };
      }
    },

    saveReport(state, action) {
      const newReport = {
        id: nanoid(),
        ...action.payload,
        createdAt: new Date().toISOString(),
        lastGenerated: new Date().toISOString(),
        isScheduled: false
      };
      state.savedReports.unshift(newReport);
    },

    updateReport(state, action) {
      const { id, ...updates } = action.payload;
      const reportIndex = state.savedReports.findIndex(r => r.id === id);
      if (reportIndex !== -1) {
        state.savedReports[reportIndex] = {
          ...state.savedReports[reportIndex],
          ...updates,
          lastModified: new Date().toISOString()
        };
      }
    },

    deleteReport(state, action) {
      const { id } = action.payload;
      state.savedReports = state.savedReports.filter(r => r.id !== id);
    },

    updateKPIs(state, action) {
      state.kpis = {
        ...state.kpis,
        ...action.payload
      };
      state.lastUpdated = new Date().toISOString();
    },

    refreshFinancialData(state) {
      state.financialData = generateFinancialData();
      state.kpis = generateKPIs();
      state.lastUpdated = new Date().toISOString();
    }
  }
});

export const {
  generateReport,
  completeReportGeneration,
  saveReport,
  updateReport,
  deleteReport,
  updateKPIs,
  refreshFinancialData
} = financeReportSlice.actions;

export default financeReportSlice.reducer;
